import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../../services/errorMessage';
import { fehlerText, sortiereMitarbeiter } from '../../../services/sifa/kundenportalLogic';
import { holeDateiUrl, ladeKundenportal, speichereMitarbeiterPortal } from '../../../services/sifa/kundenportalPublicService';
import type {
  KundenportalDaten,
  KundenportalStatus,
  PortalDateiArt,
  PortalMitarbeiterEingabe,
} from '../../../services/sifa/kundenportalTypes';
import { lesePin, sessionKey, type PortalTab } from './kundenportalSeiteLogic';

/**
 * Zustand und Handlungen der Kundenseite.
 *
 * Die PIN lebt im sessionStorage je Token — ein Neuladen fragt nicht erneut,
 * ein geschlossener Tab vergisst sie. Die Daten werden bei jedem Laden
 * frisch ueber die anon-RPC geholt (ein zwischenzeitlich deaktivierter
 * Zugang ist damit wirklich zu). Kein Login, keine Tabelle: nur die RPCs aus
 * kundenportalPublicService.
 */

export type PortalStatus = 'pin' | 'laedt' | 'bereit' | 'ungueltig' | 'fehler';

const liesGespeichertePin = (token: string): string | null => {
  try {
    return lesePin(sessionStorage.getItem(sessionKey(token)));
  } catch {
    return null;
  }
};

const merkePin = (token: string, pin: string | null): void => {
  try {
    if (pin) sessionStorage.setItem(sessionKey(token), pin);
    else sessionStorage.removeItem(sessionKey(token));
  } catch {
    // Privater Modus / gesperrter Speicher: die Seite laeuft ohne Persistenz weiter.
  }
};

export const useKundenportal = (token: string) => {
  const [pin, setPin] = useState<string | null>(() => liesGespeichertePin(token));
  const [status, setStatus] = useState<PortalStatus>(() => (liesGespeichertePin(token) ? 'laedt' : 'pin'));
  const [daten, setDaten] = useState<KundenportalDaten | null>(null);
  /** Meldung auf der PIN-Seite bzw. als Banner ueber den Reitern. */
  const [meldung, setMeldung] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dateiBusyId, setDateiBusyId] = useState<string | null>(null);
  const [tab, setTab] = useState<PortalTab>('uebersicht');
  const laufendeAnmeldung = useRef(0);

  /** Fehlstatus einordnen: Link tot → 'ungueltig'; PIN/Sperre → zurueck zur PIN-Seite. */
  const behandleStatus = useCallback(
    (s: KundenportalStatus) => {
      const text = fehlerText(s);
      if (s.fehler === 'unbekannt' || s.fehler === 'inaktiv' || s.fehler === 'schema') {
        merkePin(token, null);
        setPin(null);
        setDaten(null);
        setStatus('ungueltig');
        setMeldung(text);
        return;
      }
      if (s.fehler === 'pin' || s.fehler === 'gesperrt') {
        merkePin(token, null);
        setPin(null);
        setDaten(null);
        setStatus('pin');
        setMeldung(text);
        return;
      }
      setMeldung(text);
    },
    [token],
  );

  const anmelden = useCallback(
    async (eingabe: string) => {
      const p = (eingabe || '').trim();
      const lauf = ++laufendeAnmeldung.current;
      setBusy(true);
      setStatus('laedt');
      setMeldung(null);
      try {
        const ergebnis = await ladeKundenportal(token, p);
        if (lauf !== laufendeAnmeldung.current) return;
        // `=== true` statt blosser Wahrheitspruefung — nachgemessen, nicht
        // vermutet: `if (ergebnis.ok)` laesst `ergebnis` im Sonst-Zweig die
        // ganze Union und tsc meldet in Zeile 95
        //   TS2345: Argument of type 'LadeErgebnis' is not assignable to
        //   parameter of type 'KundenportalStatus'.
        // Grund ist die tsconfig ohne strictNullChecks: die Wahrheitspruefung
        // wirft den Zweig `{ ok: true }` dort nicht aus der Union. Der
        // Vergleich gegen das Literal `true` tut es.
        if (ergebnis.ok === true) {
          merkePin(token, p);
          setPin(p);
          setDaten(ergebnis.daten);
          setStatus('bereit');
          return;
        }
        behandleStatus(ergebnis);
      } catch (e) {
        if (lauf !== laufendeAnmeldung.current) return;
        setMeldung(describeError(e));
        setStatus(pin ? 'fehler' : 'pin');
      } finally {
        if (lauf === laufendeAnmeldung.current) setBusy(false);
      }
    },
    [behandleStatus, pin, token],
  );

  // Gespeicherte PIN: beim ersten Rendern direkt anmelden.
  useEffect(() => {
    const gespeichert = liesGespeichertePin(token);
    if (gespeichert) void anmelden(gespeichert);
    // Nur einmal je Token — `anmelden` aendert sich mit `pin`, das darf keinen zweiten Lauf ausloesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const neuLaden = useCallback(async () => {
    if (!pin) return;
    await anmelden(pin);
  }, [anmelden, pin]);

  const abmelden = useCallback(() => {
    merkePin(token, null);
    setPin(null);
    setDaten(null);
    setMeldung(null);
    setStatus('pin');
    setTab('uebersicht');
  }, [token]);

  /** Person anlegen/aendern; bei Erfolg wird die Liste an Ort und Stelle aktualisiert. */
  const mitarbeiterSpeichern = useCallback(
    async (eingabe: PortalMitarbeiterEingabe): Promise<{ ok: boolean; text: string | null }> => {
      if (!pin) return { ok: false, text: 'Bitte zuerst anmelden.' };
      try {
        const ergebnis = await speichereMitarbeiterPortal(token, pin, eingabe);
        if (ergebnis.ok === false) {
          if (ergebnis.fehler !== 'eingabe') behandleStatus(ergebnis);
          return { ok: false, text: fehlerText(ergebnis) };
        }
        const m = ergebnis.mitarbeiter;
        setDaten((d) => (d ? { ...d, mitarbeiter: sortiereMitarbeiter([...d.mitarbeiter.filter((x) => x.id !== m.id), m]) } : d));
        return { ok: true, text: null };
      } catch (e) {
        return { ok: false, text: describeError(e) };
      }
    },
    [behandleStatus, pin, token],
  );

  /**
   * Datei holen: signierte URL vom Endpunkt, dann dorthin navigieren — die
   * URL traegt `download=<name>`, der Browser speichert statt zu wechseln.
   * Kein `window.open` nach einem await (Popup-Blocker auf dem Handy).
   */
  const dateiHerunterladen = useCallback(
    async (art: PortalDateiArt, id: string) => {
      if (!pin || dateiBusyId) return;
      setDateiBusyId(id);
      setMeldung(null);
      try {
        const ergebnis = await holeDateiUrl(token, pin, art, id);
        if (ergebnis.ok === false) {
          if (ergebnis.fehler === 'pin' || ergebnis.fehler === 'gesperrt' || ergebnis.fehler === 'unbekannt' || ergebnis.fehler === 'inaktiv') {
            behandleStatus(ergebnis);
          } else {
            setMeldung(fehlerText(ergebnis));
          }
          return;
        }
        window.location.assign(ergebnis.url);
      } catch (e) {
        setMeldung(describeError(e));
      } finally {
        setDateiBusyId(null);
      }
    },
    [behandleStatus, dateiBusyId, pin, token],
  );

  return {
    status,
    daten,
    meldung,
    busy,
    dateiBusyId,
    tab,
    setTab,
    anmelden,
    neuLaden,
    abmelden,
    mitarbeiterSpeichern,
    dateiHerunterladen,
    meldungSchliessen: () => setMeldung(null),
  };
};

export type KundenportalSteuerung = ReturnType<typeof useKundenportal>;
